import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft } from '@mui/icons-material';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import { BlindLevel } from 'components/SaloonLobby/blindLevels';
import { SitgoSeats } from 'components/SaloonLobby/useLobbyFilters';

import styles from './FilterDrawer.module.scss';

type DrawerTab = 'cash' | 'sitgo' | 'tournament';

const GAMES_BY_TAB: Record<DrawerTab, { id: string; label: string }[]> = {
  cash: [
    { id: 'holdem', label: "Texas Hold'em" },
    { id: 'omaha', label: 'Omaha' },
    { id: 'hilo', label: 'Omaha Hi/Lo' }
  ],
  // Sit & Go is Hold'em only — there are no Omaha sit & gos, so it isn't offered.
  sitgo: [{ id: 'holdem', label: "Texas Hold'em" }],
  tournament: [
    // id matches the TOURNAMENT_CHIPS / chipSelections key ('reentry'); the
    // label still reads "NLH" to mirror Unity's lobby filter.
    { id: 'reentry', label: 'NLH' },
    { id: 'bounty', label: 'Bounty' },
    { id: 'freezeout', label: 'Freezeout' },
    { id: 'freeroll', label: 'Freeroll' },
    { id: 'satellite', label: 'Satellite' }
  ]
};

const TABLE_SIZES = [
  { id: 'heads-up', label: 'Heads - up' },
  { id: '3-6', label: '3 - 6 Max' },
  { id: '9-max', label: '9 Max' }
];

// The Sit & Go tab's seat toggle (EN-10747), labelled as the tab's own pills
// so the two read as one control: each format toggles on or off by itself.
const SITGO_SEAT_SIZES: { id: SitgoSeats; label: string }[] = [
  { id: 'headsUp', label: 'Heads-up' },
  { id: 'sixSeat', label: 'Six-seat' }
];

export interface SeatToggleState {
  selected: SitgoSeats[];
  onToggle: (seats: SitgoSeats) => void;
}

// The open tab's own picks: the parent hands each tab its own, so a pick on
// one tab never reaches another.
export interface DrawerFilters {
  // Selected blind-level ids (see blindLevels.ts). Empty = no blind filter.
  blinds: Set<string>;
  sizes: Set<string>;
}

const DEFAULT_FILTERS: DrawerFilters = {
  blinds: new Set(),
  sizes: new Set()
};

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  // The drawer's Cash/Sit & Go/Tournament tabs ARE the lobby format — selecting
  // one switches the lobby screen behind the modal (via onFormatChange) and
  // determines which games/sections render. There is no separate in-drawer tab
  // state, so the tabs and the bottom nav can never drift apart.
  format: DrawerTab;
  onFormatChange: (format: DrawerTab) => void;
  filters: DrawerFilters;
  onChange: (next: DrawerFilters) => void;
  // Cash blind-level options, derived from the cash tables actually in the lobby
  // so every available stake (incl. off-grid blinds) is selectable. Cash tab only.
  blindLevels?: BlindLevel[];
  // Game-chip and hide-toggle state is SHARED with the inline TypeRow chips
  // so the drawer and the inline chips reflect the same filter, not parallel ones.
  selectedGames: Set<string>;
  hideFull: boolean;
  onToggleGame: (id: string) => void;
  onToggleHideFull: () => void;
  // Present only while the `sitgoSeatToggle` flag is on. On the Sit & Go tab
  // it replaces the Table Size sizes with the seat toggle, SHARED with the
  // tab's pills like the game chips are; absent, the section is unchanged.
  seatToggle?: SeatToggleState;
  // Restores the active format to its shipped defaults (games re-selected,
  // hide toggles off, that tab's blinds/sizes cleared — no other tab's).
  // Owned by the parent since it spans both chip state and drawer state.
  onClearToDefault: () => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  format,
  onFormatChange,
  filters,
  onChange,
  blindLevels = [],
  selectedGames,
  hideFull,
  onToggleGame,
  onToggleHideFull,
  seatToggle,
  onClearToDefault
}) => {
  if (!open) return null;

  const toggleSize = (id: string) => {
    const next = new Set(filters.sizes);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...filters, sizes: next });
  };

  // On Sit & Go with the flag on, Table Size IS the seat toggle; everywhere
  // else it is the multi-select size filter.
  const sizeOptions =
    format === 'sitgo' && seatToggle
      ? SITGO_SEAT_SIZES.map((s) => ({
          ...s,
          on: seatToggle.selected.includes(s.id),
          onClick: () => seatToggle.onToggle(s.id)
        }))
      : TABLE_SIZES.map((s) => ({
          ...s,
          on: filters.sizes.has(s.id),
          onClick: () => toggleSize(s.id)
        }));

  // Multi-select blind levels: toggling a level id in/out of the set. An empty
  // set means "no blind filter" so every stake passes (all stakes shown by
  // default). The options come from `blindLevels`, derived from the cash tables
  // actually in the lobby, so every available stake is selectable.
  const toggleBlind = (id: string) => {
    const next = new Set(filters.blinds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ ...filters, blinds: next });
  };

  // Reset goes through the parent so it can clear chip state (games +
  // hide-full/hide-empty/hide-empty-days) alongside the drawer's blinds/sizes.
  const clearAll = () => onClearToDefault();

  // Portal to document.body so `position: fixed` resolves against the viewport.
  // The lobby lives inside the transformed SlideContainer panel, which would
  // otherwise become the containing block for fixed positioning and pin the
  // modal to the panel's box instead of the screen center.
  // Wrap the portaled content in ForcePortrait so the drawer rotates WITH the
  // portrait-locked lobby on mobile-landscape. It portals to <body> (outside the
  // lobby's wrapper), so the rotation has to live inside the portal itself.
  return createPortal(
    <ForcePortrait>
      <div className={styles.drawerRoot}>
        <div className={styles.backdrop} onClick={onClose} aria-hidden />
        <div className={`${styles.sheet} ${styles.sheetOpen}`} role='dialog' aria-modal='true'>
          <div className={styles.topbar}>
            <button
              type='button'
              className={styles.back}
              onClick={onClose}
              aria-label='Close filters'>
              <ChevronLeft />
            </button>
            <h2 className={styles.title}>Table Filters</h2>
            <span aria-hidden />
          </div>

          <div className={styles.tabs}>
            {(['cash', 'sitgo', 'tournament'] as DrawerTab[]).map((t) => (
              <button
                type='button'
                key={t}
                className={`${styles.tab} ${format === t ? styles.tabActive : ''}`}
                onClick={() => onFormatChange(t)}>
                {t === 'cash' ? 'Cash' : t === 'sitgo' ? 'Sit and Go' : 'Tournament'}
              </button>
            ))}
          </div>

          <div className={styles.body}>
            <div className={styles.section}>
              <span className={styles.sectionLabel}>Games</span>
              <div className={styles.checkRow}>
                {GAMES_BY_TAB[format].map((g) => {
                  const on = selectedGames.has(g.id);
                  return (
                    <button
                      type='button'
                      key={g.id}
                      className={`${styles.checkChip} ${on ? styles.checkChipOn : ''}`}
                      onClick={() => onToggleGame(g.id)}
                      aria-pressed={on}>
                      <span className={styles.checkBox}>
                        {on && <span className={styles.checkMark}>✓</span>}
                      </span>
                      <span>{g.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {format === 'cash' && blindLevels.length > 0 && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Blinds</span>
                <div className={`${styles.checkRow} ${styles.blindRow}`}>
                  {blindLevels.map((b) => {
                    const on = filters.blinds.has(b.id);
                    return (
                      <button
                        type='button'
                        key={b.id}
                        className={`${styles.checkChip} ${on ? styles.checkChipOn : ''}`}
                        onClick={() => toggleBlind(b.id)}
                        aria-pressed={on}>
                        <span className={styles.checkBox}>
                          {on && <span className={styles.checkMark}>✓</span>}
                        </span>
                        <span>{b.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(format === 'cash' || format === 'sitgo') && (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Table Size</span>
                <div className={styles.sizeRow}>
                  {sizeOptions.map((s) => (
                    <button
                      type='button'
                      key={s.id}
                      className={`${styles.sizeChip} ${s.on ? styles.sizeChipOn : ''}`}
                      onClick={s.onClick}
                      aria-pressed={s.on}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.section}>
              <button
                type='button'
                className={styles.toggleRow}
                onClick={onToggleHideFull}
                aria-pressed={hideFull}>
                <span className={`${styles.radio} ${hideFull ? styles.radioOn : ''}`} />
                <span className={styles.toggleLabel}>Hide Full Tables</span>
              </button>
              {/* "Hide Empty Days" was here. It is unconditional now (owner,
                  2026-09-10): an empty day never renders, so there was nothing
                  left for the setting to do. */}
            </div>

            <div className={styles.clearWrap}>
              <button type='button' className={styles.clear} onClick={clearAll}>
                Clear to default
              </button>
            </div>
          </div>
        </div>
      </div>
    </ForcePortrait>,
    document.body
  );
};

export default FilterDrawer;
export { DEFAULT_FILTERS };
